import { useEffect, useRef } from 'react';
import type { FeatureGroup, Path, PathOptions } from '@anlan/gs-map';
import { useLeafletContext } from './context';
import { useEventHandlers } from './events';
import { useLayerLifecycle } from './layer';
import { withPane } from './pane';

import type { LeafletElement, ElementHook } from './element';
import type { InteractiveLayerProps } from './layer';

export interface PathProps extends InteractiveLayerProps {
    pathOptions?: PathOptions
}

type StyledPath = FeatureGroup | Path;

export function usePathOptions(element: LeafletElement<StyledPath>, props: PathProps) {
    const optionsRef = useRef<PathOptions | undefined>(undefined);
    useEffect(function updatePathOptions() {
        if (props.pathOptions !== optionsRef.current) {
            const options = props.pathOptions ?? {};
            element.instance.setStyle(options);
            optionsRef.current = options;
        }
    }, [
        element,
        props
    ]);
}

export function createPathHook<E extends StyledPath, P extends PathProps>(useElement: ElementHook<E, P>) {
    return function usePath(props: P) {
        const context = useLeafletContext();
        const elementRef = useElement(withPane(props, context), context);
        useEventHandlers(elementRef.current, props.eventHandlers);
        useLayerLifecycle(elementRef.current, context);
        usePathOptions(elementRef.current, props);
        return elementRef;
    };
}
